<template>
    <b-overlay :show="isActiveChatLoading">
        <div id="active-team-chat-log">
            <infinite-loading
                v-if="!isActiveChatScrolling && !isLoadingFinished"
                direction="top"
                :distance="0"
                :force-use-infinite-wrapper="true"
                @infinite="infiniteHandler"
            >
                <div slot="no-more"></div>
                <div slot="no-results"></div>
            </infinite-loading>

            <div
                v-for="(entryGrp, index) in formattedActiveChatData"
                :key="String(index)"
                class="chat"
                :class="{
                    'chat-left': entryGrp.direction === 'in',
                    'chat-log-event text-center': entryGrp.type === 'event',
                }"
            >
                <div
                    v-if="entryGrp.type !== 'event' && entryGrp.direction === 'in'"
                    class="d-flex flex-row user chat-body"
                >
                    <div class="chat-avatar">
                        <b-avatar
                            size="md"
                            variant="info"
                            class="badge-minimal avatar-border-2 mr-1 round-large"
                            badge
                            :badge-variant="entryGrp.sender && resolveUserStatus(entryGrp.sender.status)"
                            :src="entryGrp.sender && entryGrp.sender.profilePicture"
                            badge-offset="-3px"
                        />
                    </div>

                    <div class="flex-fill">
                        <div v-if="activeChat.type === 'group'" class="chat-sender-name">
                            {{ entryGrp.sender && entryGrp.sender.name }}
                        </div>
                        <component
                            :is="`ActiveTeamChatLogEntries${startCase(entry.type)}`"
                            v-for="entry in entryGrp.entries"
                            :key="`${entry.type}_${entry.id}`"
                            :entry="entry"
                            :status="resolveEntryStatus(entry.ticks)"
                            :group-direction="entryGrp.direction"
                            :formatted-active-chat-data="formattedActiveChatData"
                            :active-chat="activeChat"
                        />
                    </div>
                </div>
                <div v-else class="chat-body">
                    <component
                        :is="`ActiveTeamChatLogEntries${startCase(entry.type)}`"
                        v-for="entry in entryGrp.entries"
                        :key="`${entry.type}_${entry.id}`"
                        :entry="entry"
                        :status="resolveEntryStatus(entry.ticks)"
                        :group-direction="entryGrp.direction"
                        :formatted-active-chat-data="formattedActiveChatData"
                        :active-chat="activeChat"
                    />
                </div>
            </div>
            <team-chat-typing-indicator :active-chat="activeChat" />

            <div v-observe-visibility="handleShowScrollToBottomButton" class="observable" />

            <div
                v-if="scrollToBottomButtonVisible"
                id="scroll-to-bottom"
                class="text-right position-sticky fixed-bottom"
            >
                <b-button class="btn-icon shadow-none mb-1" variant="secondary" @click="scrollToBottomInActiveChatLog">
                    <font-awesome-icon icon="arrow-down" />
                </b-button>
            </div>
        </div>
    </b-overlay>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import useTeamChat from "../useTeamChat";
import { BAvatar, BButton, BImg, BOverlay } from "bootstrap-vue";
import TeamChatLeftSidebar from "../team-chat-left-sidebar/teamChatLeftSidebar.vue";
import useActiveTeamChat from "../useActiveTeamChat";
import VueObserveVisibility from "vue-observe-visibility";
import ActiveTeamChatLogEntriesText from "../active-team-chat-log-entries/text";
import ActiveTeamChatLogEntriesAsset from "../active-team-chat-log-entries/asset";
import ActiveTeamChatLogEntriesEvent from "../active-team-chat-log-entries/event";
import TeamChatTypingIndicator from "./teamChatTypingIndicator.vue";
import { getUserData } from "@/auth/getUserData";
import { startCase } from "lodash";
import InfiniteLoading from "vue-infinite-loading";
import { resolveUserStatus } from "@/utils/users";

export default {
    components: {
        BButton,
        BImg,
        BAvatar,
        BOverlay,
        TeamChatLeftSidebar,
        ActiveTeamChatLogEntriesText,
        ActiveTeamChatLogEntriesAsset,
        ActiveTeamChatLogEntriesEvent,
        TeamChatTypingIndicator,
        InfiniteLoading,
    },
    directives: {
        VueObserveVisibility,
    },
    props: {
        activeChat: {
            type: Object,
            required: true,
        },
    },
    methods: {
        scrollToBottomInActiveChatLog() {
            this.$emit("scroll-to-bottom");
        },
        handleShowScrollToBottomButton(isVisible) {
            this.scrollToBottomButtonVisible = !isVisible;
        },
        async infiniteHandler($state) {
            const { activeChat, loadPage, perPage, isLoadingFinished, page } = useActiveTeamChat();
            if (!activeChat.value.entries) {
                return;
            }
            if (activeChat.value.entries.length >= perPage) {
                loadPage(page.value + 1, activeChat.value).then((data) => {
                    if (data < perPage) {
                        $state.complete();
                        isLoadingFinished.value = true;
                    } else {
                        page.value += 1;
                        $state.loaded();
                    }
                });
            } else {
                $state.complete();
                isLoadingFinished.value = true;
            }
        },
    },
    emits: ["scroll-to-bottom"],
    setup(props) {
        const { createForm, toggleCreateTeamChat } = useTeamChat();
        const { isActiveChatLoading, isActiveChatScrolling, isLoadingFinished } = useActiveTeamChat();
        const scrollToBottomButtonVisible = ref(false);

        const formattedActiveChatData = computed(() => {
            const chat = props.activeChat;

            const findParticipantField = (userId, field = "name") => {
                if (!Array.isArray(chat.participants)) {
                    return " ";
                }

                const participant = chat.participants.find(
                    (participant) => Number(participant.userId) === Number(userId)
                );

                if (participant) {
                    return participant?.user[field];
                } else if (userId === getUserData().id) {
                    return getUserData()[field];
                } else {
                    return " ";
                }
            };

            const formattedActiveChatLog = [];

            if (!chat?.entries?.length) {
                return formattedActiveChatLog;
            }

            let chatMessageSenderId = Number(chat.entries[0]?.senderUserId);
            let chatMessageDirection =
                chat.entries[0].type !== "event"
                    ? getUserData().id === Number(chat.entries[0].senderUserId)
                        ? "out"
                        : "in"
                    : undefined;
            let entryGroup = {
                type: chat.entries[0].type,
                direction: chatMessageDirection,
                sender:
                    chat.entries[0].type !== "event"
                        ? {
                              id: chatMessageSenderId,
                              name: findParticipantField(chat.entries[0].senderUserId),
                              status: findParticipantField(chat.entries[0].senderUserId, "status"),
                              profilePicture: findParticipantField(chat.entries[0].senderUserId, "pictureUrl"),
                          }
                        : undefined,
                entries: [],
            };

            chat.entries.forEach((entry, index) => {
                const direction =
                    entry.type !== "event"
                        ? getUserData().id === Number(entry.senderUserId)
                            ? "out"
                            : "in"
                        : undefined;
                if (chatMessageDirection === direction && Number(chatMessageSenderId) === Number(entry.senderUserId)) {
                    entryGroup.entries.push(entry);
                } else {
                    chatMessageSenderId = entry.senderUserId;
                    chatMessageDirection = direction;

                    formattedActiveChatLog.push(entryGroup);
                    entryGroup = {
                        type: entry.type,
                        direction,
                        sender:
                            entry.type !== "event"
                                ? {
                                      id: chatMessageSenderId,
                                      name: findParticipantField(entry.senderUserId),
                                      status: findParticipantField(entry.senderUserId, "status"),
                                      profilePicture: findParticipantField(entry.senderUserId, "pictureUrl"),
                                  }
                                : undefined,
                        entries: [entry],
                    };
                }
                if (index >= chat.entries.length - 1) {
                    formattedActiveChatLog.push(entryGroup);
                }
            });

            return formattedActiveChatLog;
        });

        const resolveEntryStatus = (ticks) => {
            if (!ticks) {
                return "sent";
            }

            const order = ["sent", "delivered", "read"];

            return ticks.reduce((previous, current) => {
                return order.indexOf(previous.status) < order.indexOf(current.status) ? previous : current;
            }).status;
        };

        return {
            createForm,
            isActiveChatLoading,
            isActiveChatScrolling,
            scrollToBottomButtonVisible,
            formattedActiveChatData,
            isLoadingFinished,
            resolveUserStatus,
            toggleCreateTeamChat,
            resolveEntryStatus,
            startCase,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-context.scss";
@import "~@/assets/scss/modules/team-chat/activeTeamChat.scss";
</style>
